<template>
  <div
    :class="cardClasses"
    class="sam-card"
    data-samora
    v-on="interactive ? $listeners : {}"
  >
    <div ref="mainCard">
      <template v-if="$slots.default">
        <slot />
      </template>
      <template v-else>
        <div
          ref="cardToPopulate"
          class="sam-card__content"
        >
          <div
            v-if="hasHeader"
            class="sam-card__header"
            data-testid="sam-card__header"
          >
            <slot
              v-if="$slots.header"
              name="header"
            />
            <template v-else>
              <div class="sam-card__header-title">
                <div class="sam-card__header-group">
                  <SamIcon
                    v-if="icon"
                    class="sam-card__icon"
                    :icon="icon"
                    :color="iconColor"
                  />
                  <!-- @slot Generic slot for the header title label -->
                  <slot
                    v-if="$slots['header-title']"
                    name="header-title"
                  />
                  <h6
                    v-else
                    class="sam-card__title"
                    :bablic-exclude="dontTranslateTitle"
                    :title="title"
                  >
                    {{ title }}
                    <template v-if="count">
                      <SamBadge
                        class="sam-card__badge"
                        :value="count"
                      />
                    </template>
                  </h6>
                  <slot name="title-append" />
                </div>
                <div class="sam-card__header-actions d-flex">
                  <!-- @slot Generic slot for actions in top right corner -->
                  <slot name="header-actions" />
                </div>
              </div>
              <div
                v-if="$slots['header-subInfo']"
                class="sam-card__header-sub-info"
              >
                <!-- @slot Slot under title, since there is
                sometimes subtext or components here -->
                <slot name="header-subInfo" />
              </div>
            </template>
          </div>
          <div
            v-if="hasTopSeparator"
            class="sam-card__separator"
          />
          <div
            v-if="$slots.body"
            class="sam-card__body"
          >
            <!-- @slot Slot for the actual content -->
            <slot name="body" />
          </div>
          <div
            v-if="hasBottomSeparator"
            class="sam-card__separator"
          />
          <div
            v-if="hasFooter"
            class="sam-card__footer"
          >
            <!-- @slot Bottom Slot for some further actions.
            Should only be used for action buttons. eg. cancel/Back/Next etc -->
            <slot name="footer" />
          </div>
        </div>
      </template>
    </div>
    <transition
      :duration="500"
      name="fade"
    >
      <div
        v-if="loading"
        class="sam-card__overlay elevation--2"
        data-testid="sam-card__overlay"
      />
    </transition>
  </div>
</template>

<script>
import SamBadge from '../SamBadge/SamBadge.vue';
import SamIcon from '../SamIcon/SamIcon.vue';

export default {
  name: 'SamCard',
  components: {
    SamIcon,
    SamBadge,
  },
  props: {
    /**
     * The Card title
     */
    title: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * Gives click event & box-shadow to the card
     * @values true, false
     */
    interactive: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * The badge count value
     */
    count: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    /** If the different card sections should be separated by horizontal lines
     * @values true, false
     */
    hasSeparators: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Card Icon name. Depends on the Icon component, where new icons should be added as SVGs
     */
    icon: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * Card Icon color.
     */
    iconColor: {
      type: String,
      required: false,
      default: 'currentColor',
    },
    /** If the card should be loading
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** If the card should be compact (i.e., no outer padding)
     * @values true, false
     */
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
    dontTranslateTitle: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    cardClasses() {
      return {
        'is-interactive': this.interactive,
        'is-compact': this.compact,
        'has-separators': this.hasSeparators,
      };
    },
    /*
     * Check if header is populated
     */
    hasHeader() {
      return [
        this.$slots.header,
        this.$slots['header-actions'],
        this.$slots['header-subInfo'],
        this.title,
      ].some((el) => !!el);
    },
    /*
     * Check if body slot is populated
     */
    hasBody() {
      return !!this.$slots.body;
    },
    /*
     * Check if footer slot is populated
     */
    hasFooter() {
      return !!this.$slots.footer;
    },
    /*
     * Check if top separator should be rendered
     */
    hasTopSeparator() {
      return this.hasSeparators && this.hasHeader && (this.hasBody || this.hasFooter);
    },
    /*
     * Check if bottom separator should be rendered
     */
    hasBottomSeparator() {
      return this.hasSeparators && this.hasFooter && this.hasBody;
    },
  },
};
</script>

<style lang="scss" scoped>
$card-x-padding: $size--6;

.sam-card {
  @extend .text--body-1;
  width: 100%;
  background-color: $pure--white;
  border: $size--0--fourth solid $neutral--30;
  border-radius: $size--1;
  position: relative;

  &__content {
    @extend .d-flex, .flex-column;
    row-gap: $size--4;
  }

  &.has-separators {
    &:not(.is-compact) .sam-card__separator {
      margin: 0 (-$card-x-padding);
    }

    .sam-card__separator {
      height: $size--0--fourth;
      background: $neutral--30;
    }
  }

  &__header {
    @extend .d-flex;
    position: relative;
    flex-flow: column;
  }

  &__body {
    position: relative;
  }

  &__footer {
    position: relative;
  }

  &__icon {
    width: $size--6;
    height: $size--6;
  }

  &__title {
    @extend .text--ellipse;
    color: $pure--black;
  }

  &__badge {
    vertical-align: middle;
  }

  &__header-group {
    @extend .d-flex, .flex-center, .mr-3;
    column-gap: $size--2;
    overflow: hidden;
  }

  &__header-title {
    @extend .col-xs, .d-inline-flex, .between-xs, .px-0;
  }

  &__header-sub-info {
    @extend .mt-1, .text--caption;
  }

  &__headerRight {
    &:last-child {
      margin-left: $size--2;
    }
  }

  &.is-interactive {
    @extend .shadow--card;
    transition: box-shadow $default-time $default-easing;
    cursor: pointer;

    &:hover {
      @include mix-shadow--modal;
    }
  }

  &:not(.is-compact) {
    padding: $size--4 $card-x-padding;
  }

  /* Loading classes */
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    top: $size--0;
    left: $size--0;
    border-radius: $size--1;
    border-color: $neutral--10;
    opacity: 1;
    @include skeleton-blink;
  }

  /* Animations */
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-leave-to {
    opacity: 0;
  }
}
</style>
